import { createContext, useContext, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ApiConfig, ChatbotRequest } from '@ev-tech/search-modules-api';

import type { ChatbotTranslationResolver } from '../types';
import { ChatbotStoreContext, createChatbotStore, type ChatbotStore } from '../stores/useChatbot';
import { useChatbotRequest } from '../hooks/useChatbotRequest';

export type ChatbotProviderProps = {
  apiConfig: ApiConfig;
  conversationKey: string;
  translationResolver: ChatbotTranslationResolver;
  context?: ChatbotRequest['context'];
  queryClient?: QueryClient;
  children: ReactNode;
};

type ChatbotSession = ReturnType<typeof useChatbotRequest> & {
  t: ChatbotTranslationResolver;
};

const SessionContext = createContext<ChatbotSession | null>(null);

export const useChatbotSession = () => {
  const session = useContext(SessionContext);

  if (!session) throw new Error('Chatbot must be inside ChatbotProvider.');

  return session;
};

const ChatbotSessionProvider = ({
  translationResolver,
  children,
  ...requestConfig
}: Omit<ChatbotProviderProps, 'queryClient' | 'conversationKey'> & { store: ChatbotStore }) => {
  const request = useChatbotRequest(requestConfig);

  return <SessionContext.Provider value={{ t: translationResolver, ...request }}>{children}</SessionContext.Provider>;
};

const ProviderInstance = ({ queryClient, conversationKey, children, ...config }: ChatbotProviderProps) => {
  const [client] = useState(() => queryClient ?? new QueryClient());
  const [store] = useState(() => createChatbotStore(conversationKey));

  return (
    <QueryClientProvider client={client}>
      <ChatbotStoreContext.Provider value={store}>
        <ChatbotSessionProvider {...config} store={store}>
          {children}
        </ChatbotSessionProvider>
      </ChatbotStoreContext.Provider>
    </QueryClientProvider>
  );
};

export const ChatbotProvider = (props: ChatbotProviderProps) => <ProviderInstance key={props.conversationKey} {...props} />;
